import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { X, Tag as TagIcon, Users, FolderOpen, Package, Hash, GripVertical, Receipt } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { Tag } from "@shared/schema";

interface TagManagerProps {
  projectId: string;
  position: { x: number; y: number };
  onClose: () => void;
}

type TagCategory = "Client Name" | "Related Project" | "Order Number (BID)" | "Software Used" | "Tags";

const categoryIcons: Record<TagCategory, React.ComponentType<any>> = {
  "Client Name": Users,
  "Related Project": FolderOpen,
  "Order Number (BID)": Receipt,
  "Software Used": Package,
  "Tags": Hash,
};

export function TagManager({ projectId, position, onClose }: TagManagerProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [draggedTag, setDraggedTag] = useState<Tag | null>(null);
  const [dragOverCategory, setDragOverCategory] = useState<TagCategory | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<TagCategory | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; tag: Tag | null; category: TagCategory | null }>({ 
    open: false, 
    tag: null, 
    category: null 
  });
  const [confirmCreateDialog, setConfirmCreateDialog] = useState<{ open: boolean; name: string; category: TagCategory | null }>({ 
    open: false, 
    name: "", 
    category: null 
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const restorePickerFocus = () => {
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const dismissCategoryConfirmation = () => {
    setConfirmDialog({ open: false, tag: null, category: null });
    restorePickerFocus();
  };

  const dismissCreateConfirmation = () => {
    setConfirmCreateDialog({ open: false, name: "", category: null });
    restorePickerFocus();
  };

  // Focus the input when component mounts
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // A Radix overlay is a sibling of role="alertdialog", not its child.
      // While either nested confirmation owns the screen, no document-level
      // pointer event may dismiss the picker behind it.
      if (confirmDialog.open || confirmCreateDialog.open) {
        return;
      }
      
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [confirmCreateDialog.open, confirmDialog.open, onClose]);

  // Fetch project tags
  const { data: projectTags = [], refetch: refetchProjectTags } = useQuery<Tag[]>({
    queryKey: [`/api/projects/${projectId}/tags`],
  });

  // Fetch all tags from database
  const { data: allTags = [] } = useQuery<Tag[]>({
    queryKey: [`/api/tags`],
  });

  // Filter tags based on search query and selected category
  const searchResults = searchQuery.trim()
    ? allTags.filter(tag => {
        const matchesSearch = tag.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory ? tag.category === selectedCategory : true;
        return matchesSearch && matchesCategory;
      })
    : selectedCategory 
      ? allTags.filter(tag => tag.category === selectedCategory)
      : allTags;

  // Filter out already assigned tags from search results
  const availableTags = searchResults.filter(
    tag => !projectTags.find(pt => pt.id === tag.id)
  );

  // Create new tag mutation
  const createTagMutation = useMutation({
    mutationFn: async ({ name, category }: { name: string; category: TagCategory }) => {
      const response = await apiRequest("POST", "/api/tags", { name, category, projectIds: [], usageCount: 0 });
      return response.json();
    },
    onSuccess: (newTag) => {
      // Immediately assign the new tag to the project
      assignTagMutation.mutate(newTag.id);
      setSearchQuery("");
      setSelectedCategory(null);
    },
  });

  // Update tag category mutation
  const updateTagCategoryMutation = useMutation({
    mutationFn: async ({ tagId, category }: { tagId: number; category: TagCategory }) => {
      const response = await apiRequest("PATCH", `/api/tags/${tagId}/category`, { category });
      return response.json();
    },
    onSuccess: () => {
      // Force refetch project tags to get updated categories
      refetchProjectTags();
      queryClient.invalidateQueries({ queryKey: [`/api/projects/${projectId}/tags`] });
      queryClient.invalidateQueries({ queryKey: ["/api/tags"] });
    },
  });

  // Assign tag to project mutation
  const assignTagMutation = useMutation({
    mutationFn: async (tagId: number) => {
      const response = await apiRequest("POST", `/api/projects/${projectId}/tags/${tagId}`);
      return response.json();
    },
    onSuccess: () => {
      refetchProjectTags();
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/years"] });
      setSearchQuery("");
    },
  });

  // Remove tag from project mutation with deletion logic
  const removeTagMutation = useMutation({
    mutationFn: async (tagId: number) => {
      // First, get the tag details to check its usage
      const tagResponse = await apiRequest("GET", `/api/tags/${tagId}`);
      const tag = await tagResponse.json();
      
      // Remove from current project
      const removeResponse = await apiRequest("DELETE", `/api/projects/${projectId}/tags/${tagId}`);
      
      // Check if this tag is only used by this project
      const otherProjects = tag.projectIds.filter((pid: string) => pid !== projectId);
      
      // If tag is not used by any other projects, delete it completely
      if (otherProjects.length === 0) {
        await apiRequest("DELETE", `/api/tags/${tagId}`);
      }
      
      return removeResponse.json();
    },
    onSuccess: () => {
      refetchProjectTags();
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/years"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tags"] });
    },
  });

  // Group tags by category
  const tagsByCategory = projectTags.reduce((acc, tag) => {
    const category = (tag.category as TagCategory) || "Tags";
    if (!acc[category]) acc[category] = [];
    acc[category].push(tag);
    return acc;
  }, {} as Record<TagCategory, Tag[]>);

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, tag: Tag) => {
    // Only allow dragging tags from the "Tags" category
    if (tag.category !== "Tags") {
      e.preventDefault();
      return;
    }
    setDraggedTag(tag);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, category: TagCategory) => {
    e.preventDefault();
    if (draggedTag && category !== "Tags") {
      e.dataTransfer.dropEffect = "move";
      setDragOverCategory(category);
    }
  };

  const handleDragLeave = () => {
    setDragOverCategory(null);
  };

  const handleDrop = (e: React.DragEvent, category: TagCategory) => {
    e.preventDefault();
    if (draggedTag && category !== "Tags" && draggedTag.category === "Tags") {
      setConfirmDialog({ open: true, tag: draggedTag, category });
    }
    setDragOverCategory(null);
    setDraggedTag(null);
  };

  const confirmCategoryChange = () => {
    if (confirmDialog.tag && confirmDialog.category) {
      updateTagCategoryMutation.mutate({ 
        tagId: confirmDialog.tag.id, 
        category: confirmDialog.category 
      });
    }
    setConfirmDialog({ open: false, tag: null, category: null });
    restorePickerFocus();
  };

  const confirmCreateTag = () => {
    if (confirmCreateDialog.name && confirmCreateDialog.category) {
      createTagMutation.mutate({ 
        name: confirmCreateDialog.name, 
        category: confirmCreateDialog.category 
      });
    }
    setConfirmCreateDialog({ open: false, name: "", category: null });
    restorePickerFocus();
  };

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, availableTags.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      
      if (availableTags.length > 0 && selectedIndex < availableTags.length) {
        // Select from suggestions
        assignTagMutation.mutate(availableTags[selectedIndex].id);
      } else if (searchQuery.trim() && availableTags.length === 0) {
        // Create new tag if no matches
        const tagExists = searchResults.some(
          tag => tag.name.toLowerCase() === searchQuery.trim().toLowerCase()
        );
        
        if (!tagExists) {
          const category = selectedCategory || "Tags";
          if (selectedCategory && selectedCategory !== "Tags") {
            // Show confirmation dialog for creating in specific category
            setConfirmCreateDialog({ open: true, name: searchQuery.trim(), category });
          } else {
            createTagMutation.mutate({ name: searchQuery.trim(), category });
          }
        }
      }
    } else if (e.key === "Escape") {
      if (confirmDialog.open || confirmCreateDialog.open) return;
      onClose();
    }
  }, [availableTags, selectedIndex, searchQuery, searchResults, assignTagMutation, createTagMutation, confirmCreateDialog.open, confirmDialog.open, onClose]);

  // Calculate position to avoid going off-screen
  const calculatePosition = () => {
    const maxWidth = 320;
    const maxHeight = 400;
    let { x, y } = position;

    // Adjust if would go off right edge
    if (x + maxWidth > window.innerWidth) {
      x = window.innerWidth - maxWidth - 10;
    }

    // Adjust if would go off bottom edge
    if (y + maxHeight > window.innerHeight) {
      y = window.innerHeight - maxHeight - 10;
    }

    return { left: x, top: y };
  };

  const style = calculatePosition();

  // Reorder categories - Tags first, then others
  const categories: TagCategory[] = ["Tags", "Client Name", "Related Project", "Order Number (BID)", "Software Used"];

  return createPortal(
    <>
      <div
        ref={containerRef}
        className="fixed z-[9999] bg-background border rounded-lg shadow-lg p-3 w-96"
        style={style}
      >
        <div className="space-y-2">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium">
              <TagIcon className="h-4 w-4" />
              <span>Project Tags</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={onClose}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>

          {/* Search Input - Moved to top */}
          <div className="relative">
            {selectedCategory && (
              <div className="flex items-center gap-2 mb-2 text-xs">
                <span className="text-muted-foreground">Searching in:</span>
                <Badge variant="outline" className="text-xs">
                  {selectedCategory}
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 ml-auto"
                  onClick={() => setSelectedCategory(null)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}
            <Input
              ref={inputRef}
              type="text"
              placeholder={selectedCategory ? `Search or create in ${selectedCategory}...` : "Type to search or create tags..."}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSelectedIndex(0);
              }}
              onKeyDown={handleKeyDown}
              className="h-8 text-sm"
            />
            
            {/* Suggestions - Now positioned directly under search */}
            {searchQuery && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-background border rounded-md shadow-lg z-10 max-h-48 overflow-y-auto">
                <div className="space-y-1 p-1">
                  {availableTags.map((tag, index) => {
                    const Icon = categoryIcons[(tag.category as TagCategory) || "Tags"];
                    return (
                      <div
                        key={tag.id}
                        className={`px-2 py-1 text-sm rounded cursor-pointer transition-colors ${
                          index === selectedIndex
                            ? "bg-accent text-accent-foreground"
                            : "hover:bg-accent/50"
                        }`}
                        onClick={() => assignTagMutation.mutate(tag.id)}
                        onMouseEnter={() => setSelectedIndex(index)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon className="h-3 w-3 text-gray-400" />
                            <span>{tag.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">{tag.category}</span>
                            <span className="text-xs text-muted-foreground">
                              {tag.usageCount} uses
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Create new tag option */}
                  {searchQuery.trim() && 
                   !searchResults.some(tag => 
                     tag.name.toLowerCase() === searchQuery.trim().toLowerCase()
                   ) && (
                    <div
                      className={`px-2 py-1 text-sm rounded cursor-pointer transition-colors ${
                        selectedIndex === availableTags.length
                          ? "bg-accent text-accent-foreground"
                          : "hover:bg-accent/50"
                      }`}
                      onClick={() => {
                        const category = selectedCategory || "Tags";
                        if (selectedCategory && selectedCategory !== "Tags") {
                          setConfirmCreateDialog({ open: true, name: searchQuery.trim(), category });
                        } else {
                          createTagMutation.mutate({ name: searchQuery.trim(), category });
                        }
                      }}
                      onMouseEnter={() => setSelectedIndex(availableTags.length)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Create:</span>
                        <Badge variant="outline" className="text-xs">
                          {searchQuery.trim()}
                        </Badge>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Categories with Tags */}
          <ScrollArea className="max-h-96">
            <div className="space-y-3">
              {categories.map(category => {
                const Icon = categoryIcons[category];
                const categoryTags = tagsByCategory[category] || [];
                const isDropTarget = dragOverCategory === category;
                const isTags = category === "Tags";
                
                return (
                  <div
                    key={category}
                    className={`border rounded-lg p-2 transition-colors ${
                      isDropTarget ? "border-blue-500 bg-blue-500/10" : "border-gray-600"
                    }`}
                    onDragOver={(e) => handleDragOver(e, category)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, category)}
                  >
                    {/* Category header */}
                    <div 
                      className="flex items-center gap-2 mb-1 cursor-pointer hover:bg-accent/20 rounded px-1 -mx-1"
                      onClick={() => {
                        setSelectedCategory(category);
                        setSearchQuery("");
                        inputRef.current?.focus();
                      }}
                    >
                      <Icon className="h-3.5 w-3.5 text-gray-400" />
                      <span className="text-xs font-medium text-gray-300">
                        {isTags ? "Custom Tags" : category}
                      </span>
                      {!isTags && (
                        <span className="text-xs text-gray-500 ml-auto">Click to search / Drop here</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {categoryTags.map(tag => {
                        // Check if tag is used by only one project (grey) or multiple (blue)
                        const isSingleUse = tag.projectIds && tag.projectIds.length === 1;
                        
                        return (
                          <Badge
                            key={tag.id}
                            variant="secondary"
                            style={{
                              backgroundColor: isSingleUse ? '#6b7280' : '#579dff',  // Grey for single use, light blue for multiple
                              color: 'white'
                            }}
                            className={`text-xs transition-colors ${
                              isTags 
                                ? "cursor-move hover:opacity-80" 
                                : "hover:bg-destructive hover:text-destructive-foreground"
                            } flex items-center`}
                            draggable={isTags}
                            onDragStart={(e) => handleDragStart(e, tag)}
                          >
                            {isTags && <GripVertical className="h-3 w-3 mr-1" />}
                            <span>{tag.name}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeTagMutation.mutate(tag.id);
                              }}
                              className="ml-1 hover:opacity-70"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        );
                      })}
                      {categoryTags.length === 0 && !isTags && (
                        <span className="text-xs text-gray-500 italic">
                          Drop tags here
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>



          {/* Help text */}
          <div className="text-xs text-muted-foreground pt-1 border-t">
            Enter to select • Click category to search • Drag to categorize • Esc to close
          </div>
        </div>
      </div>

      {/* Confirmation Dialog for Category Assignment */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => {
        if (!open) {
          dismissCategoryConfirmation();
        }
      }}>
        <AlertDialogPrimitive.Portal>
          <AlertDialogPrimitive.Overlay
            className="fixed inset-0 z-[10000] bg-black/80 pointer-events-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            data-testid="tag-category-confirm-overlay"
          />
          <AlertDialogPrimitive.Content
            className="fixed left-[50%] top-[50%] z-[10001] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg pointer-events-auto duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg"
            data-testid="tag-category-confirm-dialog"
          >
          <AlertDialogHeader>
            <AlertDialogTitle>Assign Tag to Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently assign "{confirmDialog.tag?.name}" to the "{confirmDialog.category}" category? 
              This change will affect all projects using this tag.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCategoryChange}>
              Confirm Assignment
            </AlertDialogAction>
          </AlertDialogFooter>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Portal>
      </AlertDialog>

      {/* Confirmation Dialog for Creating Tag in Category */}
      <AlertDialog open={confirmCreateDialog.open} onOpenChange={(open) => {
        if (!open) {
          dismissCreateConfirmation();
        }
      }}>
        <AlertDialogPrimitive.Portal>
          <AlertDialogPrimitive.Overlay
            className="fixed inset-0 z-[10000] bg-black/80 pointer-events-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            data-testid="tag-create-confirm-overlay"
          />
          <AlertDialogPrimitive.Content
            className="fixed left-[50%] top-[50%] z-[10001] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg pointer-events-auto duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg"
            data-testid="tag-create-confirm-dialog"
          >
          <AlertDialogHeader>
            <AlertDialogTitle>Create Tag in {confirmCreateDialog.category}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to create "{confirmCreateDialog.name}" in the "{confirmCreateDialog.category}" category? 
              This tag will be permanently assigned to this category and available for all projects.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCreateTag}>
              Create Tag
            </AlertDialogAction>
          </AlertDialogFooter>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Portal>
      </AlertDialog>
    </>,
    document.body
  );
}